<script setup>
import {
  humanReadableTimestamp,
  humanReadableDuration,
  parseDuration,
} from '@/composables/datetime';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import ArtifactPayload from '@/components/artifacts/ArtifactPayload.vue';
import { useArtifactStore } from '@/store/artifacts';
import { humanReadableFilesize } from '@/composables/filesizeHelpers.js';
import { useInstanceStore } from '@/store/backendInstances';
import { writeToClipboard } from '@/composables/clipboard';
import { useToast } from 'vue-toastification';
import { isPigBrother } from '@/composables/version.js'

const props = defineProps({ artifactId: { type: String, default: '' } });

const artifactStore = useArtifactStore();
const instanceStore = useInstanceStore();
const toast = useToast();
const { t } = useI18n();

const fullscreenPayload = ref(false);

const artifact = computed(() => {
  if (!artifactStore.artifacts[props.artifactId]) return null;
  return artifactStore.artifacts[props.artifactId].data;
});

const controlRecord = computed(() => {
  if (!artifactStore.artifacts[props.artifactId]) return null;
  return artifactStore.artifacts[props.artifactId].controlRecord;
});

const artifactTitle = computed(() => {
  return `${t('artifact.type.' + artifact.value.type)}, ${humanReadableTimestamp(artifact.value.timestamp)}`;
});

const hasDuration = computed(() => {
  if (!artifact.value) return false;
  if (!artifact.value.data) return false;
  if (artifact.value.data.time) return true;
  return !!artifact.value.data.durationSeconds;
});

const humanReadableDurationTime = computed(() => {
  if (!hasDuration.value) return null;
  let duration;
  if (artifact.value.data.time) {
    duration = parseDuration(artifact.value.data.time.duration);
  } else {
    duration = artifact.value.data.durationSeconds * 1000;
  }
  return humanReadableDuration(duration);
})

const isDownloaded = computed(() => {
  if (!controlRecord.value) return false;
  return controlRecord.value.downloaded;
});

const isDownloading = computed(() => {
  if (!controlRecord.value) return false;
  return controlRecord.value.downloading;
});

const shareUrl = computed(() => {
  return `${window.location.origin}/instance/${instanceStore.selectedInstanceId}/artifact/${props.artifactId}`;
});

async function downloadPayload() {
  await artifactStore.downloadArtifactPayload(props.artifactId);
}

async function savePayload() {
  if (!isDownloaded.value) return;
  artifactStore.savePayload(props.artifactId);
}

async function downloadAndSavePayload() {
  await downloadPayload();
  await savePayload();
}

async function share(url, title='BarnBuddy') {
  try {
    return navigator.share({
      url: url,
      title: title,
    });
  } catch {
    const copyResult = await writeToClipboard(url);
    if (copyResult) {
      toast.success(t('toast.copied_to_clipboard'));
    } else {
      toast.error(t('toast.copy_failed'));
    }
  }
}
</script>

<template>
  <v-row no-gutters>
    <!-- image -->
    <v-col
      cols="5"
      :class="{ 'cursor-pointer': isDownloaded }"
      @click="() => isDownloaded && (fullscreenPayload = true)"
    >
      <ArtifactPayload
        :artifact-id="artifact.artifactId"
        :hide-controls="true"
        is-mini-artifact
      />
    </v-col>
    <v-col
      cols="7"
      class="pl-4"
    >
      <div class="h-100 d-flex flex-column justify-space-between">
        <div>
          <p class="text-h6 text-truncate">
            {{ t('artifact.type.' + artifact.type) }}
          </p>
          <p class="text-subtitle-2 text-truncate">
            {{ humanReadableTimestamp(artifact.timestamp) }}
          </p>
          <p class="text-subtitle-2 text-truncate">
            {{
              `${humanReadableDurationTime} · ${
                artifact.size
                  ? humanReadableFilesize(artifact.size)
                  : artifact.status
              }`
            }}
          </p>
        </div>

        <div class="d-flex flex-row align-center ga-2">
          <!-- Download -->
          <v-btn
            v-if="
              !isDownloaded &&
                (artifact?.status === 'done' ||
                  artifact?.status === 'archived')
            "
            icon
            size="x-small"
            :disabled="isDownloading"
            @click="downloadPayload"
          >
            <v-icon icon="mdi-download" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ $t("general_interface.buttons.download") }}
            </v-tooltip>
          </v-btn>

          <!-- Save -->
          <v-btn
            v-if="isDownloaded"
            icon
            size="x-small"
            @click="savePayload"
          >
            <v-icon icon="mdi-content-save-outline" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ $t("general_interface.buttons.save") }}
            </v-tooltip>
          </v-btn>

          <!-- Download & Save -->
          <v-btn
            v-if="
              !isDownloaded &&
                (artifact?.status === 'done' ||
                  artifact?.status === 'archived')
            "
            icon
            size="x-small"
            :disabled="isDownloading"
            @click="downloadAndSavePayload"
          >
            <v-icon icon="mdi-content-save-outline" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ $t("general_interface.buttons.download_and_save") }}
            </v-tooltip>
          </v-btn>

          <!-- Fullscreen -->
          <v-btn
            v-if="isDownloaded"
            icon
            size="x-small"
            @click="fullscreenPayload = true"
          >
            <v-icon icon="mdi-fullscreen" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ $t("general_interface.buttons.fullscreen") }}
            </v-tooltip>
            <v-dialog
              v-model="fullscreenPayload"
              fullscreen
              height="100dvh"
            >
              <v-card
                class="d-flex flex-column"
                style="max-height: 100%;"
                max-width="100%"
              >
                <div class="d-flex flex-row justify-space-between align-center px-4 text-truncate">
                  <div class="px-4 flex-shrink-1 text-truncate d-flex align-center ga-4">
                    <v-icon icon="mdi-image-area" />
                    <v-card-title class="px-0 py-4 flex-shrink-1 text-truncate">
                      {{ artifactTitle }}
                    </v-card-title>
                  </div>
                  <v-btn
                    icon="mdi-close"
                    size="small"
                    variant="plain"
                    @click="fullscreenPayload = false"
                  />
                </div>

                <div
                  class="flex-fill"
                  style="max-height: 100%; overflow: hidden;"
                >
                  <ArtifactPayload
                    v-if="fullscreenPayload"
                    :artifact-id="artifact.artifactId"
                    full-screen
                  />
                </div>

                <v-card-actions class="justify-end">
                  <v-btn
                    v-if="isDownloaded"
                    prepend-icon="mdi-content-save-outline"
                    @click="savePayload"
                  >
                    {{ $t("general_interface.buttons.save") }}
                  </v-btn>
                  <v-btn
                    prepend-icon="mdi-close"
                    @click="fullscreenPayload = false"
                  >
                    {{ $t("general_interface.buttons.close") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-btn>
          <v-btn
            icon
            size="x-small"
            @click="share(shareUrl, artifactTitle)"
          >
            <v-icon icon="mdi-share-variant" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              {{ $t("general_interface.buttons.share") }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </v-col>
  </v-row>
</template>
